import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Typography
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from '../../../../services/ApiService';
import { User } from '../../../types/UserTypes';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { SelectChangeEvent } from '@mui/material';
import { AssignWorkflowRequest } from '../../../types/ProgramTypes';

interface LocationState {
  userId: number;
}

function SubscribeWorkflow() {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [programsMap, setProgramsMap] = useState<Map<number, string>>(new Map());
  const [workflowsMap, setWorkflowsMap] = useState<Map<number, string>>(new Map());
  const [selectedProgram, setSelectedProgram] = useState<string>('0');
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('0');

  const location = useLocation();
  const navigate = useNavigate();
  const { userId } = location.state as LocationState;

  useEffect(() => {
    if (userId) {
      fetchUserDetails();
      fetchProgramsMap();
    }
  }, [userId]);

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/getUserDetails/${userId}`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user details.');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgramsMap = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/getProgramsMap');
      const entries = Object.entries(response.data).map(([k, v]): [number, string] => [Number(k), String(v)]);
      setProgramsMap(new Map(entries));
    } catch (error) {
      console.error('Error fetching programs.');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflowsMap = async (programId: number) => {
    try {
      setLoading(true);
      const response = await axios.get(`/getWorkflowsMap/${programId}`);
      const entries = Object.entries(response.data).map(([k, v]): [number, string] => [Number(k), String(v)]);
      setWorkflowsMap(new Map(entries));
      setSelectedWorkflow('0');
    } catch (error) {
      console.error('Error fetching workflows.');
    } finally {
      setLoading(false);
    }
  };

  const handleProgramChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedProgram(value);
    if (Number(value)) {
      fetchWorkflowsMap(Number(value));
    } else {
      setWorkflowsMap(new Map());
    }
  };

  const handleSubmit = async () => {
    if (!selectedProgram || selectedProgram === '0') {
      toast.error('Please select a program');
      return;
    }

    if (!selectedWorkflow || selectedWorkflow === '0') {
      toast.error('Please select a workflow');
      return;
    }

    try {
      setLoading(true);
      const payload: AssignWorkflowRequest = {
        workflowId: Number(selectedWorkflow),
        userId: userId
      };
      
      const response = await axios.post('/assignWorkflow', payload);
      console.log('Workflow assigned successfully');
      toast.success('Workflow assigned successfully too');
      navigate(-1);
    } catch (error) {
      console.error('Error assigning workflow.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return <LoadingSpinner loading={loading} />;

  return (
    <>
      <Helmet>
        <title>BVT - Workflow Assignment</title>
      </Helmet>
      <Box sx={{ padding: 3 }}>
        <Card>
          <CardHeader
            title="Workflow Assignment"
            subheader={`${user.firstName} ${user.lastName}`}
          />
          <Divider />
          <CardContent>
            {programsMap.size === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="textSecondary">
                  No Workflows available to subscribe
                </Typography>
              </Box>
            ) : (
              <Stack spacing={3}>
                <FormControl fullWidth>
                  <InputLabel>Select Program</InputLabel>
                  <Select
                    value={selectedProgram}
                    label="Select Program"
                    onChange={handleProgramChange}
                  >
                    <MenuItem value={0}>Select a program</MenuItem>
                    {Array.from(programsMap.entries()).map(([id, name]) => (
                      <MenuItem key={id} value={id.toString()}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Select Workflow</InputLabel>
                  <Select
                    value={selectedWorkflow}
                    label="Select Workflow"
                    onChange={(e) => setSelectedWorkflow(e.target.value)}
                    disabled={!selectedProgram}
                  >
                    <MenuItem value={0}>Select a workflow</MenuItem>
                    {Array.from(workflowsMap.entries()).map(([id, name]) => (
                      <MenuItem key={id} value={id.toString()}>
                        {name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            )}
          </CardContent>
          <Divider />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, gap: 1 }}>
            <Button
              variant="contained"
              color="inherit"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            {programsMap.size > 0 && (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!selectedProgram || !selectedWorkflow}
              >
                Submit
              </Button>
            )}
          </Box>
        </Card>
      </Box>
      <LoadingSpinner loading={loading} />
    </>
  );
}

export default SubscribeWorkflow; 